// NoteComposer — the one place a person writes a note: the input half of a notes
// trail whose entries `EntityTimeline` draws (FRONTEND-PATTERNS §Files' Notes —
// the comment convention). A box ("Write a note"); the Add button appears with
// the first word; Ctrl/⌘+Enter adds. The note is written where notes are read —
// never a drawer, never a button in the surface's top row. The host owns where
// the note goes (`onAdd`) and whether the person may write (it mounts the box or
// not); key it by the record so a step to the next one starts empty. The save
// is awaited: the box holds still while the note is on its way, empties once it
// lands, and a refusal is the one refusal line above the box, the text kept
// (ADR-084 §6 — never a toast over an emptied box; a box gone meanwhile — the
// record stepped — says it in a toast). Rung-2 composite, props-only; the words are its own (every place
// says them alike).

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { Textarea } from '../ui/Textarea.js';
import { RefusalLine } from './forms/RefusalLine.js';
import { useRefusalLine } from './forms/useRefusalLine.js';
import { useRefusalToast } from './forms/useQuickSave.js';

export interface NoteComposerProps {
  /** The note's text, trimmed — the host saves it; a promise is awaited (a rejection is the box's line, the text kept). */
  onAdd: (text: string) => Promise<unknown> | void;
  className?: string;
}

export function NoteComposer({ onAdd, className = '' }: NoteComposerProps) {
  const { t } = useTranslation('common');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const line = useRefusalLine({ edit: false });
  const refusedLate = useRefusalToast('note-add');
  // A box keyed by its record unmounts on a step to the next one: a refusal that lands after has no box to stand in.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const add = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    line.dismiss();
    setBusy(true);
    try {
      await onAdd(text);
      setDraft('');
    } catch (err) {
      if (mounted.current) line.refuse(err);
      else refusedLate(err);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <form
      className={`flex flex-col gap-2 ${className}`}
      onSubmit={(e) => {
        e.preventDefault();
        void add();
      }}
    >
      <RefusalLine line={line} />
      <Textarea
        aria-label={t('notes.label')}
        placeholder={t('notes.placeholder')}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void add();
          }
        }}
        readOnly={busy}
        rows={2}
      />
      {draft.trim() !== '' && (
        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={busy}>
            {t('notes.add')}
          </Button>
        </div>
      )}
    </form>
  );
}
